import { Fragment, ReactNode, useEffect, useMemo, useState } from 'react';

import { useMeApi } from '~/lib/api/me/use-me-api';
import { PrePayRecord } from '~/lib/api/me/types';
import { P2GBox, P2GButton, P2GChip, P2GDate, P2GGrid, P2GText } from '@parcel2go/core-ui';
import { AppCard } from '~/components/card/app-card';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { P2GImageMessage } from '~/components/imagemessage';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';

interface PrePayListProps {
  wrapper: (x: ReactNode) => ReactNode;
  take?: number;
}

const PrePayList = ({ wrapper, take: initialTake }: PrePayListProps) => {
  const { getPrePayHistory } = useMeApi();
  const { t } = useTranslation(TranslationNamespaces.PREPAY);
  const [history, setHistory] = useState<PrePayRecord[]>();
  const take = initialTake ?? 8;
  const [skip, setSkip] = useState<number>(0);

  useEffect(() => {
    const load = async () => {
      const response = await getPrePayHistory(take, skip);
      if (response.success && response.result) {
        setHistory((current) =>
          (current ?? []).concat(response.result.history.filter((x) => !(current ?? []).some((c) => c.id === x.id)))
        );
      }
    };
    load();
  }, [skip]);

  const historyGroupedByYearAndMonth = useMemo(() => {
    if (!history) return null;
    const output: { [key: string]: PrePayRecord[] } = history.reduce((acc, record) => {
      const date = new Date(record.date);
      const key = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(record);
      return acc;
    }, {});

    return output;
  }, [history]);

  if (!history || !historyGroupedByYearAndMonth) {
    return wrapper(
      <P2GImageMessage
        image="/theme/common/notifications/prepay.png?v=1"
        message={t('LOADING_PREPAY_TITLE', { defaultValue: 'Loading your PrePay history...' })}
        showLoader
        contentKey="image-prepay-loading"
      />
    );
  }

  if (history.length === 0) {
    return wrapper(
      <P2GImageMessage
        image="/theme/common/notifications/prepay.png?v=1"
        message={t('NO_PREPAY_HISTORY', { defaultValue: 'You have no PrePay history' })}
        contentKey="image-prepay-empty-list"
      />
    );
  }
  return wrapper(
    <P2GGrid container direction="column" spacing={1}>
      {Object.keys(historyGroupedByYearAndMonth).map((key) => (
        <Fragment key={key}>
          <P2GGrid item mt={1} mb={1}>
            <P2GText variant="subtitle2">{key}</P2GText>
          </P2GGrid>
          {historyGroupedByYearAndMonth[key].map((record) => (
            <P2GGrid item key={record.id}>
              <AppCard>
                <P2GBox p={2}>
                  <P2GGrid container spacing={1} alignItems="center">
                    <P2GGrid item>
                      <P2GText component="div">{record.type}</P2GText>
                      <P2GText component="div" variant="caption">
                        <P2GDate variant="caption" date={new Date(record.date)} size="small" />
                        {' - '}
                        {new Date(record.date).toLocaleTimeString()}
                      </P2GText>
                    </P2GGrid>
                    <P2GGrid item flexGrow={1}>
                      {record.isBonus && <P2GChip label="Bonus" size="small" />}
                    </P2GGrid>
                    <P2GGrid item>
                      <P2GText component="div" variant="body1">
                        {toDisplayCurrency(record.code, record.amount)}
                      </P2GText>
                    </P2GGrid>
                  </P2GGrid>
                </P2GBox>
              </AppCard>
            </P2GGrid>
          ))}
        </Fragment>
      ))}
      <P2GGrid item mt={4}>
        <P2GButton onClick={() => setSkip(skip + take)}>
          {t('SHOW_MORE_BUTTON', { defaultValue: 'Show More' })}
        </P2GButton>
      </P2GGrid>
    </P2GGrid>
  );
};

export default PrePayList;
